import localforage from 'localforage';
import { formatRoid } from '../utils/imageNaming';
import type {
  ImageUploadKind,
  QueuedImageUpload,
} from './offlineImageUploadQueue';

/**
 * Avisos de imágenes perdidas de la bandeja de salida (spec
 * 2026-10-01-photo-outbox-identity § 5.4).
 *
 * Una entrada cuyo fichero ya no existe no puede subir nunca. Antes se quedaba
 * en `failed` y «Reintentar» la hacía fallar otra vez en 40 ms, para siempre,
 * con un aviso que no decía de qué orden era (tablet Tram 2, 1-oct: 9 intentos
 * seguidos). Ahora sale de la cola y deja aquí un aviso con su ROID, que el
 * banner enseña fijo hasta que el operario pulsa «Entendido».
 *
 * Persistente (localforage): sobrevive a recargas y al cierre de la app.
 * Agrupado por `(orderId, kind)`: dos fotos perdidas de la misma actuación son
 * un solo aviso con `count: 2`.
 */

const NOTICES_KEY = 'rois_offline_lost_image_notices';

/** Evento que avisa a la interfaz de que la lista de avisos cambió. */
export const LOST_IMAGES_EVENT = 'offline-lost-images-changed';

/**
 * Por qué no subirá nunca:
 * - `missing-file`: su fichero ya no está en la bandeja (spec § 5.4);
 * - `order-gone`: la orden de destino se borró mientras esperaba (spec
 *   2026-10-12-image-order-gone). El servidor lo dice con `IMAGE_ORDER_NOT_FOUND`.
 */
export type LostImageReason = 'missing-file' | 'order-gone';

export interface LostImageNotice {
  orderId: string;
  /** ROID legible de la orden (`At.0003016.0045.02`). */
  roid: string;
  kind: ImageUploadKind;
  /** Ausente en los avisos guardados antes de existir: son `missing-file`. */
  reason?: LostImageReason;
  /** Cuántas imágenes de esa orden se perdieron. */
  count: number;
  /** Última vez que se perdió una (ms). Ordena los avisos: el más antiguo primero. */
  lastAt: number;
}

export const reasonOf = (notice: Pick<LostImageNotice, 'reason'>): LostImageReason =>
  notice.reason ?? 'missing-file';

// Cerrojo propio, como el de la cola: el sync manager puede anotar varias
// pérdidas seguidas y sin él se pisarían las lecturas+escrituras.
let noticesLock: Promise<void> = Promise.resolve();

function withLock<T>(action: () => Promise<T>): Promise<T> {
  const next = noticesLock.then(action);
  noticesLock = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

export async function getLostImageNotices(): Promise<LostImageNotice[]> {
  try {
    return (await localforage.getItem<LostImageNotice[]>(NOTICES_KEY)) ?? [];
  } catch {
    return [];
  }
}

async function saveNotices(notices: LostImageNotice[]): Promise<void> {
  await localforage.setItem(NOTICES_KEY, notices);
  window.dispatchEvent(new CustomEvent(LOST_IMAGES_EVENT));
}

/**
 * Anota la pérdida de una imagen de la bandeja. Agrupa por orden, tipo y
 * motivo: no es lo mismo «vuelve a añadirla» que «esa orden ya no existe».
 */
export async function recordLostImage(
  op: Pick<QueuedImageUpload, 'orderId' | 'kind' | 'orderMetadata'>,
  reason: LostImageReason = 'missing-file',
): Promise<void> {
  return withLock(async () => {
    const notices = await getLostImageNotices();
    const existing = notices.find(
      (n) => n.orderId === op.orderId && n.kind === op.kind && reasonOf(n) === reason,
    );
    if (existing) {
      existing.count += 1;
      existing.lastAt = Date.now();
    } else {
      notices.push({
        orderId: op.orderId,
        roid: formatRoid(op.orderMetadata ?? {}),
        kind: op.kind,
        reason,
        count: 1,
        lastAt: Date.now(),
      });
    }
    await saveNotices(notices);
  });
}

/**
 * Borra todos los avisos. Es para el cierre de sesión: llevan el ROID de las
 * órdenes del operario que sale, y en una tablet compartida el siguiente los
 * veía (y podía darlos por «Entendido» sin saber de qué iban). Pasa por el
 * cerrojo y avisa a la interfaz, como cualquier otra escritura.
 */
export async function clearLostImageNotices(): Promise<void> {
  return withLock(async () => {
    await localforage.removeItem(NOTICES_KEY);
    window.dispatchEvent(new CustomEvent(LOST_IMAGES_EVENT));
  });
}

/** «Entendido»: quita el aviso de esa orden, tipo y motivo. */
export async function acknowledgeLostImageNotice(
  orderId: string,
  kind: ImageUploadKind,
  reason: LostImageReason = 'missing-file',
): Promise<void> {
  return withLock(async () => {
    const notices = await getLostImageNotices();
    await saveNotices(
      notices.filter(
        (n) => !(n.orderId === orderId && n.kind === kind && reasonOf(n) === reason),
      ),
    );
  });
}
